import React from 'react';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';

import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import {
  getGarageDataWFilter,
  getCarProtectionListing,
} from './../../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import ServiceFilterPanel, {
  EMPTY_SERVICE_FILTER,
  isServiceFilterActive,
  serviceMatchesFilter,
} from '../../../common/ServiceFilterPanel';

export default function CarProtection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const home = useSelector((state) => state?.home);
  const AllList = home?.getCarProtectionList;
  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  // [CF 2026-08-02] Package price range + open-now, matched in the browser
  // (the listing returns each vendor's packages and opening calendar).
  const [svcFilter, setSvcFilter] = useState({ ...EMPTY_SERVICE_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    dispatch(getCarProtectionListing());
  }, [dispatch]);

  const visibleList = React.useMemo(() => {
    if (!AllList) return undefined;
    let list = [...AllList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.carProtectionName || '').toLowerCase().includes(q)
      );
    }
    list = list.filter((x) => serviceMatchesFilter(x, svcFilter));
    list = applySort(list, sortOption, {
      name: (x) => x.carProtectionName,
      coords: userCoords,
    });
    return list;
  }, [AllList, sortOption, userCoords, search, svcFilter]);

  // Loading state
  if (AllList === undefined) {
    return (
      <div
        className="text-center text-warning"
        style={{ padding: '80px' }}
      >
        <PageLoader />
      </div>
    );
  }

  // Empty state (no protection services at all)
  /* [CF 2026-08-09] An early `return` used to replace the WHOLE page with a
     "nothing found" panel whenever the list came back empty — the title, the
     search box and the filter panel went with it. On the server-filtered
     categories (Car Dealers, Rent a Car) that is a dead end: filter down to
     zero results and the controls you need to widen the filter have vanished,
     so the only way out is the browser Back button. The normal render path
     already shows an in-layout empty message beneath the header, so the early
     return is gone and that message does the job with the controls on screen.
  */
  return (
    <div>
      <div className="main-content-area">
        <div className="container">
          <h2 className="mb-2">{t('Car Protection')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* [CF 2026-08-02] Filter funnel — panel opens on its own row below. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isServiceFilterActive(svcFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {filtersOpen && (
            <ServiceFilterPanel
              value={svcFilter}
              onChange={setSvcFilter}
              onApply={() => setFiltersOpen(false)}
              fields={['price', 'openNow']}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((item, i) => {
                const logo = item?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;
                return (
                  <div
                    key={item?.carProtectionId || i}
                    className="col-4 col-sm-4 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-car-wash-block">
                      <Link
                        to={`/user-car-protection-details/${item?.carProtectionId}`}
                      >
                        <div
                          className="line-a"
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            overflow: 'hidden',
                            borderRadius: '50%',   // [CF] client: all logos rounded
                            background: '#f4f4f4',
                          }}
                        >
                          <img
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            src={logo || 'assets/images/user/taxi/user.jpg'}
                            alt={item?.carProtectionName || ''}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'assets/images/user/taxi/user.jpg';
                            }}
                          />
                        </div>
                        <div className="line-b"> {item?.carProtectionName}</div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
